import { useState, useEffect, useRef } from 'react';

const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);

    useEffect(() => {
        if (didMount.current) func();
        else didMount.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

/**
 * Loads an image source and keep track of isLoaded and error states.
 *
 * @param {string} src
 * @returns {[boolean, boolean]}
 */
function useImageLoader(src) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!src) {
            return;
        }
        const image = new Image();
        image.onload = () => setIsLoaded(true);
        image.onerror = () => setError(true);
        image.src = src;
        return () => {
            image.onload = null;
            image.onerror = null;
        };
    }, [src]);

    return [isLoaded, error];
}

const intervals = {};
/**
 * Set up an periodical call to a component callback.
 * Intervals with the same period time are reused.
 * The interval can be cancelled with the returned function. It also cancels itself when no component uses it.
 *
 * @param {int} period - Milliseconds
 * @param {function} callback
 * @returns {function}
 */
function useTicker(period, callback) {
    // Remove callback from interval and cancel interval if no callbacks remain
    const cleanup = () => {
        if (intervals[period]) {
            intervals[period].callbacks = intervals[period].callbacks.filter(
                cb => cb !== callback
            );
            if (intervals[period].callbacks.length === 0) {
                clearInterval(intervals[period].intervalId);
                intervals[period] = undefined;
            }
        }
    };
    useEffect(() => {
        // Reuse existing interval
        if (intervals[period]) {
            intervals[period].callbacks.push(callback);
            // Add callback to new interval and save interval id for cancellation
        } else {
            const intervalId = setInterval(() => {
                intervals[period].callbacks.forEach(cb => cb());
            }, period);
            intervals[period] = { callbacks: [callback], intervalId };
        }
        // Return cancellation callback
        return cleanup;
    });

    return cleanup;
}

export { useDidMountEffect, useImageLoader, useTicker };
